import register from 'preact-custom-element'
import { FieldText } from './FieldText'
import { FieldEmail } from './FieldEmail'
import { FieldTel } from './FieldTel'
import { FieldConsent } from './FieldConsent'
import { AccountFitClubCard } from './AccountFitClubCard'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { useSignUpDetails } from '../hooks/useSignUpDetails'
import { accountTranslations } from '../helpers/translations'
import { useState } from 'preact/hooks'

/**
 * Fit Club landing page sign-up, embedded in the page rather than behind the drawer.
 *
 * Sign-up only by design: a returning shopper uses the "Login Instead" link, which is a plain
 * #account anchor that modal-handler.js turns into the drawer. The field set mirrors the drawer's
 * "Tell us more about you" step exactly - only the row arrangement differs, which is why the
 * markup lives here and useSignUpDetails owns the payload.
 *
 * All copy arrives as attributes so the section owns it. Read them off `props` inside the render
 * body only - preact-custom-element hands attributes over as props, but any DOM lookup for
 * Liquid-rendered content would run after the host's innerHTML has been wiped.
 */
const FitClubSignUp = (props) => {
  const [customer, setCustomer] = useState(null)

  const { formInputs, handleOnChange, handleSubmit, submitting, error } = useSignUpDetails({
    collectComms: true,
    onSuccess: setCustomer
  })

  if (customer) {
    return (
      <div className={'flex flex-col gap-4'}>
        <p className={'paragraph-lg font-bold'}>{accountTranslations.account_portal.sign_up_success_title}</p>
        <p className={'paragraph-md'}>{accountTranslations.account_portal.sign_up_success_description}</p>
        {customer.id && (
          <AccountFitClubCard memberSince={''} firstName={customer.name} barcode={customer.id} showTotal={false} />
        )}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={'flex w-full flex-col gap-6'}>
      <div className={'flex flex-col gap-2'}>
        <p className={'paragraph-xs font-bold'}>{accountTranslations.form_labels.your_name}*</p>
        <div className={'flex flex-row gap-4'}>
          <FieldText
            type="text"
            autoComplete="given-name"
            required={true}
            placeholder={accountTranslations.form_placeholders.first_name}
            name={formInputs.firstName.key}
            value={formInputs.firstName.value}
            onChange={handleOnChange}
          />
          <FieldText
            type="text"
            autoComplete="family-name"
            required={true}
            placeholder={accountTranslations.form_placeholders.last_name}
            name={formInputs.lastName.key}
            value={formInputs.lastName.value}
            onChange={handleOnChange}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-4 md:flex-row'}>
        <FieldEmail
          label={accountTranslations.form_labels.email}
          autoComplete="email"
          placeholder={accountTranslations.form_placeholders.email}
          required={true}
          name={formInputs.email.key}
          value={formInputs.email.value}
          onChange={handleOnChange}
          error={error?.type === 'email' ? error.message : null}
        />
        {/* Optional, matching the drawer's join form. */}
        <FieldTel
          label={accountTranslations.form_labels.mobile_optional}
          autoComplete="tel"
          type="tel"
          placeholder={accountTranslations.form_placeholders.mobile}
          required={false}
          name={formInputs.mobile.key}
          value={formInputs.mobile.value}
          onChange={handleOnChange}
          error={error?.type === 'mobile_phone' ? error.message : null}
        />
      </div>

      {/* Optional, and not yet sent anywhere - see docs/omneo-fit-club-auth-open-questions.md */}
      {/* Hidden For now */}
      {/* <FieldBirthday
        label={accountTranslations.form_labels.child_birthday}
        day={formInputs.childBirthDay}
        month={formInputs.childBirthMonth}
        year={formInputs.childBirthYear}
        onChange={handleOnChange}
      /> */}

      <div className={'flex flex-col gap-4'}>
        {/* Both consent rows are merchant-editable on the Fit Club Signup block. */}
        <FieldConsent
          name={formInputs.marketingOptIn.key}
          value={formInputs.marketingOptIn.value}
          onChange={handleOnChange}
          html={props.dataMarketingConsent || accountTranslations.form_labels.marketing_consent_html}
        />
        <FieldConsent
          name={formInputs.termsAccepted.key}
          value={formInputs.termsAccepted.value}
          onChange={handleOnChange}
          required={true}
          html={props.dataTermsConsent || accountTranslations.form_labels.terms_consent_html}
        />
      </div>

      {/* Accepting the terms is what enables this; btn-solid-black already carries the disabled look. */}
      <button
        className={'btn btn-solid-black btn-lg relative w-full justify-center'}
        disabled={!formInputs.termsAccepted.value || submitting}
      >
        {submitting ? <LoaderSpin /> : props.dataSubmitLabel}
      </button>

      {error?.message && <div className={'paragraph-sm font-medium text-system-error'}>{error.message}</div>}

      <div className={'flex items-center justify-center gap-2'}>
        <p className={'paragraph-sm font-bold'}>{props.dataLoginPrompt}</p>
        <a className={'paragraph-sm font-medium underline'} href="#account">
          {props.dataLoginLabel}
        </a>
      </div>
    </form>
  )
}

register(FitClubSignUp, 'x-fit-club-sign-up')

export { FitClubSignUp }
